const AES_GCM_IV_BYTES = 12;

async function importAesGcmKey(passphrase: string): Promise<CryptoKey> {
  const raw = new TextEncoder().encode(passphrase);
  return crypto.subtle.importKey(
    "raw",
    raw,
    { name: "AES-GCM", length: raw.byteLength * 8 },
    false,
    ["encrypt", "decrypt"]
  );
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export async function encryptAes(plaintext: string, passphrase: string): Promise<string> {
  const key = await importAesGcmKey(passphrase);
  const iv = crypto.getRandomValues(new Uint8Array(AES_GCM_IV_BYTES));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(plaintext)
  );
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return bytesToBase64(combined);
}

export async function decryptAes(ciphertextBase64: string, passphrase: string): Promise<string> {
  const combined = base64ToBytes(ciphertextBase64);
  if (combined.length <= AES_GCM_IV_BYTES) {
    throw new Error("Invalid ciphertext");
  }
  const iv = combined.slice(0, AES_GCM_IV_BYTES);
  const ciphertext = combined.slice(AES_GCM_IV_BYTES);
  const key = await importAesGcmKey(passphrase);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ciphertext
  );
  return new TextDecoder().decode(plaintext);
}
